import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import DramaCard from '@/components/feature/DramaCard';
import { dramas } from '@/mocks/dramas';

const PAGE_SIZE = 6;
const statusOptions = ['全部', '更新中', '已完结'] as const;
const sortOptions = [
  { key: 'latest', label: '最新更新' },
  { key: 'episodes-desc', label: '集数：从多到少' },
  { key: 'episodes-asc', label: '集数：从少到多' },
] as const;

type StatusOption = (typeof statusOptions)[number];
type SortKey = (typeof sortOptions)[number]['key'];

export default function Category() {
  const [status, setStatus] = useState<StatusOption>('全部');
  const [sort, setSort] = useState<SortKey>('latest');
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    document.title = '善良的小姨子剧集列表 - 影视免费在线观看平台';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        'content',
        '善良的小姨子剧集列表页，汇总家庭情感、都市生活等影视作品，支持按更新状态筛选与集数排序，方便查看剧情介绍与在线播放入口。',
      );
    }
  }, []);

  const filtered = useMemo(() => {
    let list = [...dramas];
    if (status !== '全部') {
      list = list.filter((d) => d.status === status);
    }
    if (sort === 'latest') {
      list.sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1));
    } else if (sort === 'episodes-desc') {
      list.sort((a, b) => b.totalEpisodes - a.totalEpisodes);
    } else {
      list.sort((a, b) => a.totalEpisodes - b.totalEpisodes);
    }
    return list;
  }, [status, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetAll = () => {
    setStatus('全部');
    setSort('latest');
    setPage(1);
  };

  const handleStatus = (value: StatusOption) => {
    setStatus(value);
    setPage(1);
  };
  const handleSort = (value: SortKey) => {
    setSort(value);
    setPage(1);
  };

  return (
    <Layout>
      <div className="pt-16 md:pt-[68px]">
        <section className="w-full px-4 md:px-16 lg:px-24 pt-8 md:pt-12 pb-6">
          <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-6">
            <Link to="/" className="hover:text-primary-700 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">分类</span>
          </nav>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                <span className="text-xs font-medium text-foreground-500 tracking-wide">剧集分类</span>
              </div>
              <h1 className="font-heading font-bold text-foreground-950 text-2xl md:text-4xl">
                善良的小姨子剧集列表及剧情介绍
              </h1>
              <p className="text-foreground-600 text-sm md:text-base mt-3 max-w-2xl leading-relaxed">
                这里汇总了“善良的小姨子”及相关家庭情感、都市生活题材影视作品。你可以按更新状态筛选、按集数排序，快速找到想看的剧集并查看剧情介绍。
              </p>
            </div>
            <div className="text-sm text-foreground-500 whitespace-nowrap">
              共 <span className="text-primary-700 font-semibold">{filtered.length}</span> 部作品
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-16 lg:px-24 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 lg:gap-8">
            <aside className="hidden lg:block">
              <div className="sticky top-24 rounded-lg border border-background-200/70 bg-background-50 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <i className="ri-filter-3-line text-primary-600" />
                  <h2 className="font-heading font-semibold text-foreground-950 text-sm">筛选与排序</h2>
                </div>
                <div className="mb-6">
                  <p className="text-xs font-medium text-foreground-500 mb-3">更新状态</p>
                  <div className="flex flex-col gap-2">
                    {statusOptions.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => handleStatus(opt)}
                        className={`flex items-center justify-between rounded-md px-3 py-2 text-sm whitespace-nowrap transition-colors cursor-pointer ${
                          status === opt
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                        }`}
                      >
                        {opt}
                        {status === opt && <i className="ri-check-line" />}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-foreground-500 mb-3">排序方式</p>
                  <div className="flex flex-col gap-2">
                    {sortOptions.map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        onClick={() => handleSort(opt.key)}
                        className={`flex items-center justify-between rounded-md px-3 py-2 text-sm whitespace-nowrap transition-colors cursor-pointer ${
                          sort === opt.key
                            ? 'bg-secondary-500 text-background-50'
                            : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                        }`}
                      >
                        {opt.label}
                        {sort === opt.key && <i className="ri-check-line" />}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </aside>

            <div>
              <div className="lg:hidden mb-4">
                <button
                  type="button"
                  onClick={() => setFilterOpen((v) => !v)}
                  className="w-full flex items-center justify-between rounded-md border border-background-200/70 bg-background-50 px-4 py-3 text-sm font-medium text-foreground-800 cursor-pointer"
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-filter-3-line text-primary-600" />
                    筛选与排序
                  </span>
                  <i className={filterOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
                </button>
                {filterOpen && (
                  <div className="mt-2 rounded-md border border-background-200/70 bg-background-50 p-4">
                    <p className="text-xs font-medium text-foreground-500 mb-2">更新状态</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {statusOptions.map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => handleStatus(opt)}
                          className={`rounded-full px-3 py-1.5 text-xs whitespace-nowrap transition-colors cursor-pointer ${
                            status === opt
                              ? 'bg-primary-500 text-background-50'
                              : 'bg-background-100 text-foreground-700'
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs font-medium text-foreground-500 mb-2">排序方式</p>
                    <div className="flex flex-wrap gap-2">
                      {sortOptions.map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => handleSort(opt.key)}
                          className={`rounded-full px-3 py-1.5 text-xs whitespace-nowrap transition-colors cursor-pointer ${
                            sort === opt.key
                              ? 'bg-secondary-500 text-background-50'
                              : 'bg-background-100 text-foreground-700'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {pageItems.length === 0 ? (
                <div className="rounded-lg border border-background-200/70 bg-background-50 py-20 flex flex-col items-center text-center px-4">
                  <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
                  <p className="mt-4 text-foreground-700 font-medium">暂无相关剧集，请尝试其他筛选条件</p>
                  <button
                    type="button"
                    onClick={resetAll}
                    className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-refresh-line" />
                    重置筛选
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                    {pageItems.map((drama) => (
                      <DramaCard key={drama.id} drama={drama} />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-10">
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={safePage === 1}
                        className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200/70 text-foreground-700 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                        aria-label="上一页"
                      >
                        <i className="ri-arrow-left-s-line" />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setPage(p)}
                          className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-medium transition-colors cursor-pointer ${
                            safePage === p
                              ? 'bg-primary-500 text-background-50'
                              : 'border border-background-200/70 text-foreground-700 hover:bg-background-100'
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={safePage === totalPages}
                        className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200/70 text-foreground-700 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                        aria-label="下一页"
                      >
                        <i className="ri-arrow-right-s-line" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}